import { FC } from 'react';
import { Content, isFilled, asText } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import { renderTextWithHighlight } from '@/utils/stringUtils';

/**
 * Question mark circle icon (teal outline).
 */
const QuestionIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="mt-0.5 shrink-0"
  >
    <circle cx="12" cy="12" r="11" stroke="#03BF9E" strokeWidth="2" />
    <text
      x="12"
      y="16.5"
      textAnchor="middle"
      fill="#03BF9E"
      fontSize="14"
      fontWeight="600"
      fontFamily="Inter, sans-serif"
    >
      ?
    </text>
  </svg>
);

/**
 * Props for `SneakPeekSelfAssessment`.
 */
export type SneakPeekSelfAssessmentProps =
  SliceComponentProps<Content.SneakPeekSelfAssessmentSlice>;

/**
 * Component for "Sneak Peek Self Assessment" Slices.
 */
const SneakPeekSelfAssessment: FC<SneakPeekSelfAssessmentProps> = ({
  slice,
}) => {
  const {
    background_image,
    heading,
    intro,
    preview_instruction,
    question_previews,
    preview_image,
  } = slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="w-full px-5 md:px-[30px]"
    >
      <div className="relative mx-auto max-w-[1380px] overflow-hidden rounded-[16px] bg-[#F5F5F0]">
        {/* Background image */}
        {isFilled.image(background_image) && (
          <div className="absolute inset-0 z-0">
            <PrismicNextImage
              field={background_image}
              fill
              className="object-cover"
            />
          </div>
        )}

        <div className="relative z-10 px-5 py-16 md:px-[100px] md:py-[80px]">
          {/* ── Top: heading + intro (full-width) ── */}
          <div className="mx-auto flex max-w-[1180px] flex-col gap-6 text-[#041118]">
            {isFilled.richText(heading) && (
              <div className="font-inter text-[32px] font-medium capitalize leading-[1.2] md:text-[42px]">
                <PrismicRichText field={heading} />
              </div>
            )}

            {isFilled.richText(intro) && (
              <div className="font-inter text-[16px] font-light leading-[1.4] md:text-[18px]">
                <PrismicRichText field={intro} />
              </div>
            )}
          </div>

          {/* ── Bottom: two-column with questions + image ── */}
          <div className="mx-auto mt-10 flex max-w-[1180px] flex-col gap-10 md:mt-[40px] md:flex-row md:items-center md:gap-[40px]">
            {/* Left: preview instruction + question list */}
            <div className="flex flex-col gap-6 md:w-[590px] md:shrink-0">
              {/* Instruction text with green highlighted portion */}
              {isFilled.richText(preview_instruction) && (
                <p className="font-inter text-[18px] font-semibold leading-[1.2] text-[#041118] md:text-[22px]">
                  {renderTextWithHighlight(asText(preview_instruction))}
                </p>
              )}

              {/* Question preview items */}
              {question_previews && question_previews.length > 0 && (
                <div className="flex flex-col gap-6">
                  {question_previews.map((item, idx) => (
                    <div key={idx} className="flex gap-4 items-start">
                      <QuestionIcon />
                      <p className="font-inter text-[16px] leading-[1.2] text-[#041118] md:text-[18px]">
                        {item.topic && (
                          <span className="font-semibold capitalize">
                            {item.topic}:{' '}
                          </span>
                        )}
                        {item.description && (
                          <span className="font-light">{item.description}</span>
                        )}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right: preview image */}
            {isFilled.image(preview_image) && (
              <div className="flex-1">
                <div className="overflow-hidden rounded-[24px]">
                  <PrismicNextImage
                    field={preview_image}
                    className="h-auto w-full object-cover"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SneakPeekSelfAssessment;
